import React, { useEffect, useRef, useState } from "react"
import { Alert, Spinner } from "reactstrap"
import { get } from "helpers/api_helper"
import { UNCONFIRMED } from "./proposalMessages"
import { organizerListPath } from "./plannerQuery"
import { publicationEmailLine, publicationResendMessage } from "./publishingLabels"
import AddressPicker, { addressesReady, recipientsOf } from "./AddressPicker"
import PlannerDialog from "./PlannerDialog"

// The addresses offered: the organizer's account and contact emails, and any other the email went to; those it went to are ticked.
const addressesOf = (organizer, email) => {
  const known = (organizer?.emails || []).map(({ email: address, role }) => ({ email: address, role }))
  const others = email.recipients.filter(address => !known.some(item => item.email === address)).map(address => ({ email: address, role: "added" }))
  return { addresses: [...known, ...others].map(item => ({ ...item, on: email.recipients.includes(item.email) })), checked: false }
}

// A publication email sent again (E1p pinned decisions, Tudor Q2; as a list's "Send again", ticket 16): the same email, by email to the
// addresses checked again, or recorded as sent by the SuperAdmin outside the platform. The published events and the proposals do not
// change. The organizer's list read gives their addresses and whether this server sends emails. A refusal is shown here.
export default function PublicationResendDialog({ email, name, commands, onClose, onSent }) {
  const [list, setList] = useState(null)
  const [how, setHow] = useState("email")
  const [address, setAddress] = useState(null)
  const [state, setState] = useState({})
  const open = useRef(true)
  useEffect(() => () => { open.current = false }, [])
  // The addresses are seeded once, from the email as it was when the dialog opened: a later delivery read does not reset them.
  const opened = useRef(email)
  useEffect(() => {
    get(organizerListPath(opened.current.organizerId, [])).then(response => {
      if (!open.current) return
      // The addresses before the list: an update outside React's batching renders at once, and the picker needs them.
      setAddress(addressesOf(response?.data?.organizer, opened.current))
      if (!response?.data?.team?.email) setHow("outsidePlatform")
      setList(response?.data || {})
    }).catch(() => open.current && setState({ error: "The organizer's addresses could not be read. Close and try again." }))
  }, [])
  const initial = list && JSON.stringify(addressesOf(list.organizer, opened.current))
  const resend = async () => {
    setState({ pending: true })
    const result = await commands.resendPublication({ publicationId: email.publicationId, how,
      ...(how === "email" && { recipients: recipientsOf(address), addressChecked: true }) }, { name, how })
    if (!open.current) return
    if (result?.status === "saved") { onSent(); onClose(); return }
    setState({ error: result?.status === "unknown" ? result.message || UNCONFIRMED : publicationResendMessage(result?.code, result?.details) })
  }
  const ready = how === "outsidePlatform" || (address && addressesReady(address))
  return <PlannerDialog title="Send the publication email again?" sub={`${name} · the same email, listing the same events`}
    dirty={how === "email" && Boolean(address) && JSON.stringify(address) !== initial} onClose={onClose}
    actions={<button type="button" className="btn btn-primary" disabled={!list || state.pending || !ready}
      title={ready ? undefined : "Choose the addresses and tick “I checked…” first."} onClick={resend}>
      {how === "email" ? "Send the email again" : "Record that I sent it"}</button>}>
    <p className="mb-0">{publicationEmailLine(email)}. The events stay published whatever happens to the email.</p>
    {!list && !state.error && <div className="ep-loading"><Spinner size="sm" /> Loading the organizer's addresses…</div>}
    {list && <>
      <span className="ep-segment" role="group" aria-label="How it goes again">
        <button type="button" aria-pressed={how === "email"} className={"ep-chip" + (how === "email" ? " is-on" : "")} disabled={!list.team?.email}
          onClick={() => setHow("email")}>By email</button>
        <button type="button" aria-pressed={how === "outsidePlatform"} className={"ep-chip" + (how === "outsidePlatform" ? " is-on" : "")}
          onClick={() => setHow("outsidePlatform")}>I told them myself</button>
      </span>
      {how === "email" ? <AddressPicker id="ep-publication-resend" value={address} team={list.team} onChange={setAddress}
        checkLabel="I checked that the email goes to the right address" />
        : <p className="ep-meta mb-0">The email is recorded as delivered by you outside the platform (WhatsApp, your own email, the phone).</p>}
    </>}
    {state.error && <Alert color="danger" className="mb-0">{state.error}</Alert>}
  </PlannerDialog>
}
